import { StatusType } from '@getaccept/fried-tofu';
import { MoveDirection } from '@getaccept/lib-shared/src/enums/move-direction';
import ActionPlanConstants from '../constants/action-plan.constants';
import type { Progress, ActionPlan, Milestone, Task } from '../types/action-plan';
import { TaskStatus } from '../types/enums/task-status';
import type { Participant } from '../../types/participant';
import AvatarHelper from '../../helpers/avatar.helper';

export default class MilestoneHelper {
  static getProgress = (progress: Progress) => {
    if (!progress?.taskCount) {
      return '0%';
    }
    const percentage = (progress.completedTaskCount / progress.taskCount) * 100;
    return `${percentage}%`;
  };

  static getActionPlanProgress = (actionPlan: ActionPlan) => {
    if (!actionPlan.milestones) {
      return ActionPlanConstants.defaultProgress;
    }

    return actionPlan.milestones?.reduce(
      (acc, milestone) => {
        const { taskCount, completedTaskCount } = MilestoneHelper.getMilestoneProgress(milestone);

        acc.milestoneCount += 1;
        acc.taskCount += taskCount;
        acc.completedTaskCount += completedTaskCount;
        acc.completedMilestoneCount += MilestoneHelper.isCompleted(milestone) ? 1 : 0;
        return acc;
      },
      {
        ...ActionPlanConstants.defaultProgress,
      }
    );
  };

  static getActionPlanProgressPercentage = (actionPlan: ActionPlan) => {
    const progress = MilestoneHelper.getActionPlanProgress(actionPlan);
    if (!progress?.taskCount) {
      return 0;
    }
    return (progress.completedTaskCount / progress.taskCount) * 100;
  };

  static getMilestoneProgress = (milestone: Milestone) => ({
    taskCount: milestone.tasks.length,
    completedTaskCount: milestone.tasks.filter(task => task.status === TaskStatus.Completed).length,
  });

  static getMilestoneProgressPercentage = (milestone: Milestone) => {
    const { taskCount, completedTaskCount } = MilestoneHelper.getMilestoneProgress(milestone);
    if (!taskCount) {
      return 0;
    }
    return (completedTaskCount / taskCount) * 100;
  };

  static completedMilestones = (actionPlan: ActionPlan) => {
    if (!actionPlan.milestones) {
      return [];
    }
    return actionPlan.milestones.filter(milestone => MilestoneHelper.isCompleted(milestone));
  };

  static isCompleted = (milestone: Milestone) => {
    if (milestone.tasks?.length === 0) {
      return false;
    }
    return milestone.tasks.every(task => task.status === TaskStatus.Completed);
  };

  static getStatus = (milestone: Milestone) => {
    if (MilestoneHelper.isCompleted(milestone)) {
      return StatusType.Success;
    }
    return milestone.tasks?.some(task => task.status === TaskStatus.Completed)
      ? StatusType.Info
      : StatusType.None;
  };

  static getDeletedTaskIds = (updatedTasks: Task[], currentTasks: Task[]) =>
    currentTasks.filter(task => !updatedTasks.find(({ id }) => id === task.id)).map(({ id }) => id);

  static getUpdatedTasks = (updatedTasks: Task[], currentTasks: Task[]) =>
    updatedTasks
      .filter(task => {
        const currentTask = currentTasks.find(({ id }) => id === task.id);
        return task.id && JSON.stringify(task) !== JSON.stringify(currentTask);
      })
      .map(task => ({
        taskId: task.id,
        body: {
          title: task.title,
          description: task.description,
          status: task.status,
          participantIds: task.participantIds,
          deadline: task.deadline,
        },
      }));

  static isParticipantAssignedAndActive = (task: Task, participant: Participant) =>
    task.participantIds.includes(participant.id) && !participant.deletedAt;

  static isTaskCompletedByRemovedParticipant = (task: Task, participant: Participant) =>
    !!participant.deletedAt &&
    task.status === TaskStatus.Completed &&
    task.statusUpdatedBy === participant.id;

  static getMilestoneTaskOwnersAvatars = (milestone: Milestone, participants: Participant[]) =>
    participants
      .filter(participant =>
        milestone.tasks?.some(
          task =>
            MilestoneHelper.isParticipantAssignedAndActive(task, participant) ||
            MilestoneHelper.isTaskCompletedByRemovedParticipant(task, participant)
        )
      )
      .map(participant => ({
        ...participant,
        thumbUrl: !participant.deletedAt ? participant.thumbUrl : '',
        name: !participant.deletedAt ? AvatarHelper.getDisplayName(participant) : '',
        imageTransform: participant.widget?.imageTransform,
      }));

  static getFarthestUncompletedTask = (milestone: Milestone, showUncompleted = false) => {
    const farthestUncompletedTask = milestone.tasks?.reduce((acc, task) => {
      if ((showUncompleted || task.status !== TaskStatus.Completed) && task.deadline) {
        if (!acc) {
          return task;
        }
        if (acc.deadline > task.deadline) {
          return acc;
        }
        return task;
      }
      return acc;
    }, null);

    if (!farthestUncompletedTask) {
      return null;
    }

    return farthestUncompletedTask;
  };

  static isMilestoneActive(milestone: Milestone) {
    return (
      !MilestoneHelper.isCompleted(milestone) &&
      milestone.tasks?.length > 0 &&
      milestone.tasks.some(task => task.status === TaskStatus.Completed)
    );
  }

  static getFirstActiveMilestoneId(actionPlan: ActionPlan) {
    if (!actionPlan.milestones) {
      return null;
    }

    const activeMilestone = actionPlan.milestones.find(milestone =>
      MilestoneHelper.isMilestoneActive(milestone)
    );
    return activeMilestone?.id;
  }

  static getUpdatedMilestonePosition = (
    currentIndex: number,
    direction: MoveDirection,
    milestones: Milestone[]
  ) => {
    switch (direction) {
      case MoveDirection.ToTop:
        return 0;
      case MoveDirection.Up:
        return currentIndex - 1;
      case MoveDirection.Down:
        return currentIndex + 1;
      case MoveDirection.ToBottom:
        return milestones.length - 1;
    }
  };
}
